import { useEffect, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { EQuoteItemStatusCode, IQuoteApiResponse } from 'types/quote.types';
import { QUOTE_STATUS_CODE } from 'types/quoteDetails.types';
import {
  IQuoteEditorFormData,
  IQuoteItemDetailsForQuoteManagement,
  IQuoteManagementSuggestedItem,
  IProcurementItem,
  IPlanMultiplierResponse,
  IQuotePricingConfig,
  IRequoteParentData,
  ISuggestedToolData,
  IToolQuotedData,
  IToolSearchResult
} from 'types/quoteEditor.types';
import { QuoteService } from 'services/quote.service';
import { QuoteEditorService } from 'services/quote-editor.service';
import { useModal } from 'hooks/useModal';
import QuoteEditorHeader from '../QuoteEditorHeader/QuoteEditorHeader';
import QuoteEditorToolList from '../QuoteEditorToolList/QuoteEditorToolList';
import QuoteItemEditor from '../QuoteItemEditor/QuoteItemEditor';
import QuoteEditorActions from '../QuoteEditorActions/QuoteEditorActions';
import QuoteEditorModuleSkeleton from '../QuoteEditorModuleSkeleton/QuoteEditorModuleSkeleton';
import QuoteConfirmationModal from '../QuoteConfirmationModal/QuoteConfirmationModal';
import QuoteLoaderModal from '../QuoteLoaderModal/QuoteLoaderModal';
import SuggestReasonModal from '../SuggestReasonModal/SuggestReasonModal';
import { isDeliveryEstimateRangeValid } from '../../utils/quoteEditorValidation.utils';
import { displayErrorNotification } from 'utils/displayNudosStandardNotifications';
import { extractErrorMessage } from 'utils/extractErrorMessage';
import { formatQuotePrice } from 'views/Quote/utils/formatQuotePrice';
import { AxiosError } from 'axios';
import { PRODUCT_CATEGORY } from 'types/category';
import { BACKOFFICE_MODULES } from 'types/configuration.types';
import { getPermissionsByModule } from 'utils/permissions';
import './QuoteEditorModule.scss';

const EMPTY_FORM_DATA: IQuoteEditorFormData = {
  minDeliveryEstimate: '',
  maxDeliveryEstimate: '',
  provider: '',
  costWithoutTax: '',
  margin: '',
  platinumPrice: '',
  finalPrice: '',
  finalPriceOverride: false,
  mpn: '',
  hasCTO: false
};

const MAX_SUGGESTIONS_PER_TOOL = 3;

/** reactKey estable para la sugerencia inicial. Evita remounts por Date.now() en cada render. */
const INITIAL_SUGGESTION_REACT_KEY = 0;

// CAMBIAR-JC: reemplazar por el id de la razón real para "primera sugerencia de ítem genérico"
const DEFAULT_GENERIC_REASON_ID = 11;

const INITIAL_SUGGESTION: ISuggestedToolData = {
  reactKey: INITIAL_SUGGESTION_REACT_KEY,
  selectedTool: null,
  formData: { ...EMPTY_FORM_DATA },
  procurementReasonId: DEFAULT_GENERIC_REASON_ID
};

/** Extrae el valor numérico de un string de moneda o porcentaje. */
export const parseCurrency = (val: string) => parseFloat(val.replace(/[^0-9.]/g, '') || '0');

/**
 * Igual que parseCurrency pero devuelve undefined cuando el string está vacío o no es numérico.
 * Se usa en guardados borrador para omitir campos vacíos en lugar de persistir 0 o NaN.
 */
export const parseCurrencyOrUndefined = (val: string): number | undefined => {
  const cleaned = val.replace(/[^0-9.]/g, '');
  if (cleaned === '') return undefined;
  const parsed = parseFloat(cleaned);
  return isNaN(parsed) ? undefined : parsed;
};

/**
 * Reconstruye IQuoteEditorFormData a partir de un ítem ya cotizado devuelto por la API.
 * Solo se pre-populan los campos que el backend retorna directamente: costo y precio final.
 * margin y platinumPrice se dejan vacíos hasta que el backend los retorne explícitamente,
 * ya que reverse-calcularlos introduce inconsistencias (especialmente con finalPriceOverride).
 * hasCTO se toma del isCTO guardado en el backend; para ítems de categoría CTO
 * se fuerza en true desde buildPrefilledToolsData, ya que la categoría lo implica.
 * @param finalPriceOverride - flag inicial. true para ítems ya cotizados (respeta el Final guardado),
 *   false para items de recotización (el Final se recalcula al editar costo/margen).
 */
const buildFormDataFromApiItem = (
  item: IQuoteManagementSuggestedItem,
  exchangeRate: number,
  finalPriceOverride: boolean = true
): IQuoteEditorFormData => {
  const costLocal = (Number(item.providerUnitPrice) || 0) * exchangeRate;
  const finalPrice = Number(item.totalPrice) || 0;

  return {
    minDeliveryEstimate: item.etaMin != null && item.etaMin > 0 ? String(item.etaMin) : '',
    maxDeliveryEstimate: item.etaMax != null && item.etaMax > 0 ? String(item.etaMax) : '',
    provider: item.provider?.name ?? '',
    providerId: item.provider?.id,
    costWithoutTax: costLocal > 0 ? costLocal.toFixed(2) : '',
    margin: item.providerMargin != null && item.providerMargin > 0 ? String(item.providerMargin) : '',
    platinumPrice:
      item.unitPriceWithTaxes != null && item.unitPriceWithTaxes > 0 ? String(item.unitPriceWithTaxes) : '',
    finalPrice: finalPrice > 0 ? finalPrice.toFixed(2) : '',
    finalPriceOverride,
    mpn: item.mpn ?? '',
    hasCTO: item.isCTO ?? false
  };
};

/** Mapea un ítem de la API a la fotografía inmutable de los valores iniciales para recotización. */
const buildParentSnapshotFromItem = (
  item: IQuoteManagementSuggestedItem,
  exchangeRate: number,
  quantity: number
): IRequoteParentData => {
  const baseForm = buildFormDataFromApiItem(item, exchangeRate);
  return {
    cost: baseForm.costWithoutTax,
    margin: baseForm.margin,
    platinum: baseForm.platinumPrice,
    finalPrice: baseForm.finalPrice,
    providerName: item.provider?.name ?? null,
    providerId: item.provider?.id ?? null,
    mpn: item.mpn ?? null
  };
};

/**
 * Construye el estado inicial de quotedToolsData pre-populando los formularios
 * con la data guardada en el backend para los ítems ya cotizados.
 * También pre-llena los ítems PENDING_QUOTE que tengan datos persistidos: los borradores
 * parciales guardados al "Guardar y salir", y (en recotización) los valores clonados del padre.
 */
export const buildPrefilledToolsData = (
  items: IQuoteItemDetailsForQuoteManagement[],
  planMultiplierValue: number,
  isRequote: boolean
): Record<number, IToolQuotedData> => {
  const result: Record<number, IToolQuotedData> = {};

  for (const item of items) {
    const isPendingQuote = item.quoteStatus.code === EQuoteItemStatusCode.PENDING_QUOTE;
    // Un ítem pendiente puede tener un borrador parcial guardado: en ese caso se pre-puebla igual.
    const hasSavedData =
      item.provider != null ||
      item.providerUnitPrice != null ||
      item.totalPrice != null ||
      item.etaMin != null ||
      item.etaMax != null ||
      (item.mpn ?? '') !== '';
    if (isPendingQuote && !isRequote && !item.isGenericProduct && !hasSavedData) continue;

    const exchangeRate = parseFloat(item.exchange?.exchangeRate ?? '0') || 1;
    const finalPriceOverrideInitial = !(isRequote && isPendingQuote);

    const suggestedTools: ISuggestedToolData[] = item.suggestedItems
      .filter(s => s.productId != null)
      .map(s => {
        const selectedTool: IToolSearchResult = {
          id: s.productId!,
          name: s.productName ?? '',
          sku: s.uid ?? '',
          imageUrl: s.imageUrl ?? undefined,
          brandImage: s.brandImage ?? undefined,
          category: s.categoryName ?? '',
          categoryId: s.categoryId,
          brand: s.brand ?? undefined,
          pills: s.pills ?? null,
          status: s.isActive ? 'active' : undefined
        };
        return {
          id: s.quoteItemId,
          reactKey: s.quoteItemId,
          selectedTool,
          formData: buildFormDataFromApiItem(s, exchangeRate),
          reason: s.procurementReasonComment,
          procurementReasonId: s.procurementReasonId
        };
      });

    if (item.isGenericProduct) {
      result[item.quoteItemId] = {
        formData: { ...EMPTY_FORM_DATA },
        suggestedTools: suggestedTools.length > 0 ? suggestedTools : [{ ...INITIAL_SUGGESTION }]
      };
    } else {
      const isCtoCategoryItem = item.categoryName === PRODUCT_CATEGORY.CTO_LAPTOPS;
      result[item.quoteItemId] = {
        formData: {
          ...buildFormDataFromApiItem(item, exchangeRate, finalPriceOverrideInitial),
          ...(isCtoCategoryItem && { hasCTO: true })
        },
        suggestedTools
      };
    }
  }

  return result;
};

/** Construye el mapa de snapshots inmutables del padre por quoteItemId, solo en modo recotización. */
const buildParentSnapshots = (
  items: IQuoteItemDetailsForQuoteManagement[],
  planMultiplierValue: number
): Record<number, IRequoteParentData> => {
  const result: Record<number, IRequoteParentData> = {};
  for (const item of items) {
    const exchangeRate = parseFloat(item.exchange?.exchangeRate ?? '0') || 1;
    result[item.quoteItemId] = buildParentSnapshotFromItem(item, exchangeRate, item.quantity);
  }
  return result;
};

/**
 * Construye el body IProcurementItem a partir del ítem seleccionado, el formulario
 * y las sugerencias asociadas al ítem genérico.
 * - Para ítems genéricos: omite campos de pricing del item principal y los delega a suggestedItems.
 * - Para ítems no genéricos: incluye el pricing completo del formulario.
 * - En modo recotización agrega priceSelection, previousFinalPrice y ajusta finalPriceOverride.
 * @param exchangeRate - Tasa de cambio (moneda local / USD) para calcular providerUnitPrice en USD.
 * @param isDraft - Cuando es true (guardado borrador), los campos de pricing vacíos se omiten del
 *   payload en lugar de enviarse como 0, para no persistir ceros falsos. El backend infiere por
 *   completitud si el ítem queda en PENDING_QUOTE o se marca cotizado.
 */
const buildProcurementItem = (
  item: IQuoteItemDetailsForQuoteManagement,
  formData: IQuoteEditorFormData,
  suggestions: ISuggestedToolData[],
  exchangeRate: number,
  isRequote: boolean = false,
  parentData?: IRequoteParentData,
  isDraft: boolean = false
): IProcurementItem => {
  const pickPrice = isDraft ? parseCurrencyOrUndefined : parseCurrency;
  const toUsd = (local?: number) => (local != null && exchangeRate > 0 ? local / exchangeRate : local);

  const suggestedItems: IProcurementItem[] = suggestions
    .filter(s => s.selectedTool !== null)
    .map(s => {
      const tool = s.selectedTool!;
      const sCostLocal = pickPrice(s.formData.costWithoutTax);
      const sCostUsd = toUsd(sCostLocal);
      const sMargin = pickPrice(s.formData.margin);
      const sPlatinum = pickPrice(s.formData.platinumPrice);
      const sFinalPrice = pickPrice(s.formData.finalPrice);
      const sFinalPriceField =
        sFinalPrice == null
          ? {}
          : s.formData.finalPriceOverride
          ? { finalPriceOverride: sFinalPrice }
          : { totalPrice: sFinalPrice };
      return {
        ...(s.id && { id: s.id }),
        productId: tool.id,
        quantity: item.quantity,
        etaMin: s.formData.minDeliveryEstimate ? parseInt(s.formData.minDeliveryEstimate) : undefined,
        etaMax: s.formData.maxDeliveryEstimate ? parseInt(s.formData.maxDeliveryEstimate) : undefined,
        providerId: s.formData.providerId,
        ...(sCostUsd != null && { providerUnitPrice: sCostUsd }),
        ...(sCostLocal != null && { providerUnitPriceLocal: sCostLocal }),
        ...(sMargin != null && { providerMargin: sMargin }),
        ...(sPlatinum != null && { unitPriceWithTaxes: sPlatinum }),
        ...sFinalPriceField,
        procurementReasonComment: null,
        procurementReasonId: s.procurementReasonId,
        mpn: s.formData.mpn || null,
        isCTO: s.formData.hasCTO
      };
    });

  if (item.isGenericProduct) {
    return {
      id: item.quoteItemId,
      quantity: item.quantity,
      procurementReasonComment: null,
      ...(isDraft && { isDraft: true }),
      ...(suggestedItems.length > 0 && { suggestedItems })
    };
  }

  const costLocal = pickPrice(formData.costWithoutTax);
  const costUsd = toUsd(costLocal);
  const margin = pickPrice(formData.margin);
  const platinum = pickPrice(formData.platinumPrice);
  const finalPrice = pickPrice(formData.finalPrice);
  const finalPriceField =
    finalPrice == null
      ? {}
      : formData.finalPriceOverride
      ? { finalPriceOverride: finalPrice }
      : { totalPrice: finalPrice };

  if (isRequote && parentData) {
    const previousFinalPriceUnit = parseCurrency(parentData.finalPrice);
    const sendOverride = formData.priceSelection === 'requoted' && formData.finalPriceOverride;

    return {
      id: item.quoteItemId,
      productId: item.productId ?? undefined,
      quantity: item.quantity,
      etaMin: formData.minDeliveryEstimate ? parseInt(formData.minDeliveryEstimate) : undefined,
      etaMax: formData.maxDeliveryEstimate ? parseInt(formData.maxDeliveryEstimate) : undefined,
      providerId: formData.providerId,
      ...(costUsd != null && { providerUnitPrice: costUsd }),
      ...(costLocal != null && { providerUnitPriceLocal: costLocal }),
      ...(margin != null && { providerMargin: margin }),
      procurementReasonComment: null,
      mpn: formData.mpn || null,
      isCTO: formData.hasCTO,
      priceSelection: formData.priceSelection,
      previousFinalPrice: previousFinalPriceUnit,
      ...(sendOverride && finalPrice != null && { finalPriceOverride: finalPrice }),
      ...(isDraft && { isDraft: true }),
      ...(suggestedItems.length > 0 && { suggestedItems })
    };
  }

  return {
    id: item.quoteItemId,
    productId: item.productId ?? undefined,
    quantity: item.quantity,
    etaMin: formData.minDeliveryEstimate ? parseInt(formData.minDeliveryEstimate) : undefined,
    etaMax: formData.maxDeliveryEstimate ? parseInt(formData.maxDeliveryEstimate) : undefined,
    providerId: formData.providerId,
    ...(costUsd != null && { providerUnitPrice: costUsd }),
    ...(costLocal != null && { providerUnitPriceLocal: costLocal }),
    ...(margin != null && { providerMargin: margin }),
    ...(platinum != null && { unitPriceWithTaxes: platinum }),
    ...finalPriceField,
    procurementReasonComment: null,
    mpn: formData.mpn || null,
    isCTO: formData.hasCTO,
    ...(isDraft && { isDraft: true }),
    ...(suggestedItems.length > 0 && { suggestedItems })
  };
};

/**
 * Módulo orquestador del editor de cotización. Gestiona el estado global
 * de la vista y coordina la interacción entre todos los subcomponentes.
 * Realiza dos peticiones en paralelo al montar: datos generales (header) e ítems del editor.
 */
const QuoteEditorModule = () => {
  const { id } = useParams<{ id: string }>();
  const { push, replace } = useHistory();
  const { state } = useLocation<{ quoteItemId: number }>();
  const preselectedQuoteItemId = state?.quoteItemId ?? null;

  const [quoteDetails, setQuoteDetails] = useState<IQuoteApiResponse | null>(null);
  const [quoteItems, setQuoteItems] = useState<IQuoteItemDetailsForQuoteManagement[] | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSendingQuote, setIsSendingQuote] = useState(false);
  const [selectedToolId, setSelectedToolId] = useState<number | null>(null);
  const [toolsStatus, setToolsStatus] = useState<Record<number, EQuoteItemStatusCode>>({});
  const [quotedToolsData, setQuotedToolsData] = useState<Record<number, IToolQuotedData>>({});
  const [dirtyItems, setDirtyItems] = useState<Record<number, true>>({});
  const [planMultiplier, setPlanMultiplier] = useState<IPlanMultiplierResponse | null>(null);
  const [parentSnapshot, setParentSnapshot] = useState<Record<number, IRequoteParentData>>({});

  const { isOpen: isConfirmationOpen, open: openConfirmation, close: closeConfirmation } = useModal();
  const { isOpen: isSuggestReasonOpen, open: openSuggestReason, close: closeSuggestReason } = useModal();

  /**
   * Carga los datos generales, ítems del editor y multiplicador de plan en secuencia.
   * Pre-popula quotedToolsData con los datos guardados de los ítems ya cotizados.
   */
  useEffect(() => {
    if (!id) return;
    setIsLoading(true);
    Promise.all([QuoteService.getQuoteDetails(Number(id)), QuoteEditorService.getQuoteEditorItems(Number(id))])
      .then(([details, items]) =>
        QuoteEditorService.getPlanMultiplier(details.organizationId).then(multiplier => {
          const initialStatus = Object.fromEntries(items.map(item => [item.quoteItemId, item.quoteStatus.code]));
          const isRequoteFlow = Boolean(details.parentQuoteId);
          const prefilledData = buildPrefilledToolsData(items, multiplier.planMultiplier, isRequoteFlow);
          setQuoteDetails(details);
          setQuoteItems(items);
          setToolsStatus(initialStatus);
          const matchedItem = preselectedQuoteItemId
            ? items.find(item => item.quoteItemId === preselectedQuoteItemId)
            : null;
          setSelectedToolId(matchedItem?.quoteItemId ?? items[0]?.quoteItemId ?? null);
          if (preselectedQuoteItemId) replace({ state: undefined });
          setPlanMultiplier(multiplier);
          setQuotedToolsData(prefilledData);
          if (isRequoteFlow) {
            setParentSnapshot(buildParentSnapshots(items, multiplier.planMultiplier));
          }
        })
      )
      .finally(() => setIsLoading(false));
  }, [id]);

  if (isLoading) return <QuoteEditorModuleSkeleton />;
  if (!quoteDetails || !quoteItems || !selectedToolId) return null;

  const isRequote = Boolean(quoteDetails.parentQuoteId);
  const selectedItem = quoteItems.find(i => i.quoteItemId === selectedToolId) ?? quoteItems[0];
  const selectedToolData = quotedToolsData[selectedToolId];
  const isCtoSelectedItem = selectedItem.categoryName === PRODUCT_CATEGORY.CTO_LAPTOPS;

  const initialFormData = {
    ...EMPTY_FORM_DATA,
    ...(isCtoSelectedItem && { hasCTO: true })
  };

  const currentFormData = selectedToolData?.formData ?? initialFormData;

  const quotePermissions = getPermissionsByModule({ moduleName: BACKOFFICE_MODULES.QUOTE });
  const isQuotePendingOrToQuote = [QUOTE_STATUS_CODE.PENDING, QUOTE_STATUS_CODE.TO_QUOTE].includes(
    quoteDetails.quoteStatus?.code ?? QUOTE_STATUS_CODE.PENDING
  );
  const isFormDisabled = !isQuotePendingOrToQuote || !quotePermissions.edit;

  const currentSuggestions: ISuggestedToolData[] =
    selectedToolData?.suggestedTools ?? (selectedItem.isGenericProduct ? [INITIAL_SUGGESTION] : []);
  const currentParentData = isRequote ? parentSnapshot[selectedToolId] : undefined;

  const pricingConfig: IQuotePricingConfig = {
    organizationId: quoteDetails.organizationId,
    taxRate: parseFloat(selectedItem?.exchange?.tax ?? '0') / 100,
    exchangeRate: parseFloat(selectedItem?.exchange?.exchangeRate ?? '0') || 1,
    planMultiplier: typeof planMultiplier?.planMultiplier === 'number' ? planMultiplier.planMultiplier : 1,
    localCurrency: selectedItem?.exchange?.currency || 'USD',
    saleCurrency: selectedItem?.saleCurrency ?? quoteDetails.summary?.saleCurrency ?? 'USD',
    countryCode: quoteDetails.requestedCountry.code,
    countryId: quoteDetails.requestedCountry.id
  };

  const quotedCount = quoteItems.filter(
    item => toolsStatus[item.quoteItemId] !== EQuoteItemStatusCode.PENDING_QUOTE
  ).length;

  const nonGenericFormComplete =
    !!currentFormData.providerId &&
    !!currentFormData.costWithoutTax &&
    !!currentFormData.margin &&
    !!currentFormData.platinumPrice &&
    !!currentFormData.finalPrice &&
    !!currentFormData.minDeliveryEstimate &&
    !!currentFormData.maxDeliveryEstimate &&
    isDeliveryEstimateRangeValid(currentFormData.minDeliveryEstimate, currentFormData.maxDeliveryEstimate) &&
    (!isRequote || !!currentFormData.priceSelection);

  /** Verifica que el formulario de una sugerencia esté completamente relleno. Los ETAs no se exigen cuando el producto está activo en tienda, ya que esos campos se ocultan en ese caso. */
  const isSuggestionComplete = (s: ISuggestedToolData) => {
    const isActiveInStore = s.selectedTool?.status === 'active';
    return (
      !!s.selectedTool &&
      (!!s.formData.providerId || isActiveInStore) &&
      !!s.formData.costWithoutTax &&
      !!s.formData.margin &&
      !!s.formData.platinumPrice &&
      !!s.formData.finalPrice &&
      (isActiveInStore ||
        (!!s.formData.minDeliveryEstimate &&
          !!s.formData.maxDeliveryEstimate &&
          isDeliveryEstimateRangeValid(s.formData.minDeliveryEstimate, s.formData.maxDeliveryEstimate)))
    );
  };

  const isSelectedItemPending = toolsStatus[selectedToolId] === EQuoteItemStatusCode.PENDING_QUOTE;
  const isSelectedItemDirty = !!dirtyItems[selectedToolId];

  const formIsValid = selectedItem.isGenericProduct
    ? currentSuggestions.length > 0 && currentSuggestions.every(isSuggestionComplete)
    : nonGenericFormComplete && currentSuggestions.every(isSuggestionComplete);

  const canMarkAsQuoted = !isFormDisabled && formIsValid && (isSelectedItemPending || isSelectedItemDirty);
  const canSendQuote = !isFormDisabled && quotedCount === quoteItems.length && Object.keys(dirtyItems).length === 0;
  const remainingSuggestions = MAX_SUGGESTIONS_PER_TOOL - currentSuggestions.length;

  /** Formatea el monto total cotizado devuelto por el backend. Muestra '-' si el campo aún no está disponible. */
  const formatTotalQuoted = (): string => {
    const total = quoteDetails.summary?.totalQuoted;
    if (total == null) return '-';
    return formatQuotePrice(total, quoteDetails.summary?.saleCurrency ?? 'USD');
  };

  /** Cambia la herramienta activa en el panel derecho. */
  const handleSelectTool = (toolId: number) => setSelectedToolId(toolId);

  /** Re-fetcha todos los ítems y reconstruye el estado tras crear una herramienta nueva desde el flujo de cotización. */
  const handleToolCreated = async () => {
    const freshItems = await QuoteEditorService.getQuoteEditorItems(Number(id));
    const freshStatus = Object.fromEntries(freshItems.map(item => [item.quoteItemId, item.quoteStatus.code]));
    const freshPrefilled = buildPrefilledToolsData(freshItems, planMultiplier?.planMultiplier ?? 1, isRequote);
    setQuoteItems(freshItems);
    setToolsStatus(freshStatus);
    setQuotedToolsData(freshPrefilled);
  };

  /** Marca el ítem activo como dirty en el estado de seguimiento. */
  const markSelectedDirty = () => setDirtyItems(prev => ({ ...prev, [selectedToolId]: true }));

  /** Actualiza el formulario de la herramienta activa y la marca como con cambios sin guardar. */
  const handleFormChange = (data: IQuoteEditorFormData) => {
    setQuotedToolsData(prev => ({
      ...prev,
      [selectedToolId]: { ...prev[selectedToolId], formData: data }
    }));
    markSelectedDirty();
  };

  /** Actualiza los datos de una sugerencia específica dentro de la herramienta genérica activa y la marca dirty. */
  const handleSuggestionChange = (updatedSuggestion: ISuggestedToolData) => {
    setQuotedToolsData(prev => {
      const prevSuggestions = prev[selectedToolId]?.suggestedTools ?? currentSuggestions;
      const updatedSuggestions = prevSuggestions.map(s =>
        s.reactKey === updatedSuggestion.reactKey ? updatedSuggestion : s
      );
      return { ...prev, [selectedToolId]: { ...prev[selectedToolId], suggestedTools: updatedSuggestions } };
    });
    markSelectedDirty();
  };

  /** Elimina una sugerencia por su reactKey, que es siempre único independientemente del estado de guardado. */
  const handleRemoveSuggestion = (reactKey: number) => {
    setQuotedToolsData(prev => {
      const prevSuggestions = prev[selectedToolId]?.suggestedTools ?? currentSuggestions;
      const remaining = prevSuggestions.filter(s => s.reactKey !== reactKey);
      return { ...prev, [selectedToolId]: { ...prev[selectedToolId], suggestedTools: remaining } };
    });
    markSelectedDirty();
  };

  /** Abre el modal de selección de motivo antes de agregar una sugerencia. */
  const handleAddSuggestion = () => {
    if (currentSuggestions.length >= MAX_SUGGESTIONS_PER_TOOL) return;
    openSuggestReason();
  };

  /** Agrega una nueva sugerencia vacía con el motivo seleccionado en el modal y marca el ítem como dirty. */
  const handleConfirmSuggestReason = (reasonId: number, reasonName: string) => {
    const newSuggestion: ISuggestedToolData = {
      reactKey: Date.now(),
      selectedTool: null,
      formData: { ...EMPTY_FORM_DATA },
      reason: reasonName,
      procurementReasonId: reasonId
    };
    handleChangeReason(reasonId, reasonName, newSuggestion);
    closeSuggestReason();
  };

  /** Actualiza el motivo de sugerencia en todas las herramientas sugeridas del ítem activo y marca el ítem como dirty. */
  const handleChangeReason = (reasonId: number, reasonName: string, newSuggestion?: ISuggestedToolData) => {
    setQuotedToolsData(prev => {
      const prevSuggestions = prev[selectedToolId]?.suggestedTools ?? currentSuggestions;
      const updatedSuggestions = prevSuggestions.map(s => ({
        ...s,
        procurementReasonId: reasonId,
        reason: reasonName
      }));
      const newSuggestions = newSuggestion ? [newSuggestion] : [];
      return {
        ...prev,
        [selectedToolId]: {
          ...prev[selectedToolId],
          suggestedTools: [...updatedSuggestions, ...newSuggestions]
        }
      };
    });
    markSelectedDirty();
  };

  /**
   * Envía los datos de cotización del ítem activo al backend, re-fetcha todos los ítems
   * y reconstruye el estado completo desde la respuesta fresca. Avanza al siguiente pendiente.
   */
  const handleMarkAsQuoted = async () => {
    const body = buildProcurementItem(
      selectedItem,
      currentFormData,
      currentSuggestions,
      pricingConfig.exchangeRate,
      isRequote,
      currentParentData
    );
    setIsSubmitting(true);
    await QuoteEditorService.updateQuoteItem(body)
      .then(() => QuoteEditorService.getQuoteEditorItems(Number(id)))
      .then(freshItems => {
        const freshStatus = Object.fromEntries(freshItems.map(item => [item.quoteItemId, item.quoteStatus.code]));
        const freshPrefilled = buildPrefilledToolsData(freshItems, planMultiplier?.planMultiplier ?? 1, isRequote);
        setQuoteItems(freshItems);
        setToolsStatus(freshStatus);
        setDirtyItems({});
        setQuotedToolsData(freshPrefilled);
        const nextItem = freshItems.find(
          item =>
            item.quoteItemId !== selectedToolId && freshStatus[item.quoteItemId] === EQuoteItemStatusCode.PENDING_QUOTE
        );
        if (nextItem) setSelectedToolId(nextItem.quoteItemId);
      })
      .catch((error: AxiosError) => {
        displayErrorNotification({ customJSXMessage: <>{extractErrorMessage(error)}</>, duration: 5000 });
      })
      .finally(() => setIsSubmitting(false));
  };

  /**
   * Persiste como borrador todos los ítems con cambios sin guardar y luego navega al detalle.
   * Cada ítem se envía con buildProcurementItem en modo borrador (isDraft), de modo que los campos
   * incompletos se omiten y el backend mantiene el ítem en PENDING_QUOTE hasta que esté completo.
   * Si algún guardado falla, muestra el error y no navega para no perder los datos del usuario.
   */
  const handleSaveAndExit = async () => {
    const dirtyIds = Object.keys(dirtyItems).map(Number);
    if (dirtyIds.length === 0) {
      push(`/quote/details/${id}`);
      return;
    }

    setIsSubmitting(true);
    const savePromises = dirtyIds.map(dirtyId => {
      const item = quoteItems.find(i => i.quoteItemId === dirtyId);
      if (!item) return Promise.resolve();
      const toolData = quotedToolsData[dirtyId];
      const formData = toolData?.formData ?? EMPTY_FORM_DATA;
      const suggestions = toolData?.suggestedTools ?? [];
      const itemExchangeRate = parseFloat(item.exchange?.exchangeRate ?? '0') || 1;
      const itemParentData = isRequote ? parentSnapshot[dirtyId] : undefined;
      const body = buildProcurementItem(item, formData, suggestions, itemExchangeRate, isRequote, itemParentData, true);
      return QuoteEditorService.updateQuoteItem(body);
    });

    const results = await Promise.allSettled(savePromises);
    setIsSubmitting(false);

    const firstError = results.find(result => result.status === 'rejected') as PromiseRejectedResult | undefined;
    if (firstError) {
      displayErrorNotification({ customJSXMessage: <>{extractErrorMessage(firstError.reason)}</>, duration: 5000 });
      return;
    }

    setDirtyItems({});
    push(`/quote/details/${id}`);
  };

  /**
   * Refresca el detalle de la cotización antes de abrir el modal de confirmación, para que el resumen
   * muestre la cantidad de herramientas cotizadas y el monto total actualizados. Es seguro porque el
   * botón solo se habilita cuando todo está cotizado y persistido (canSendQuote).
   */
  const handleSendQuote = async () => {
    setIsSubmitting(true);
    await QuoteService.getQuoteDetails(quoteDetails.id)
      .then(freshDetails => {
        setQuoteDetails(freshDetails);
        openConfirmation();
      })
      .catch((error: AxiosError) => {
        displayErrorNotification({ customJSXMessage: <>{extractErrorMessage(error)}</>, duration: 5000 });
      })
      .finally(() => setIsSubmitting(false));
  };

  /**
   * Envía la cotización al cliente con la fecha de vencimiento seleccionada
   * y navega a la vista de detalle tras el éxito.
   */
  const handleConfirmSendQuote = async (expirationDate: string) => {
    setIsSubmitting(true);
    setIsSendingQuote(true);
    closeConfirmation();
    await QuoteEditorService.sendQuoteFromProcurement(quoteDetails.id, { expirationDate: new Date(expirationDate) })
      .then(() => {
        push(`/quote/details/${id}`);
      })
      .finally(() => {
        setIsSubmitting(false);
        setIsSendingQuote(false);
      });
  };

  return (
    <div className="quoteEditorModule">
      <QuoteEditorHeader quoteDetails={quoteDetails} onSaveAndExit={handleSaveAndExit} isSaving={isSubmitting} />
      <div className="quoteEditorContent">
        <QuoteEditorToolList
          items={quoteItems}
          toolsStatus={toolsStatus}
          selectedToolId={selectedToolId}
          onSelectTool={handleSelectTool}
        />
        <QuoteItemEditor
          key={selectedToolId}
          selectedItem={selectedItem}
          currentFormData={currentFormData}
          currentSuggestions={currentSuggestions}
          remainingSuggestions={remainingSuggestions}
          maxSuggestionsPerTool={MAX_SUGGESTIONS_PER_TOOL}
          countryName={quoteDetails.requestedCountry.name}
          countryCode={quoteDetails.requestedCountry.code}
          pricingConfig={pricingConfig}
          isRequote={isRequote}
          isFormDisabled={isFormDisabled}
          parentData={currentParentData}
          onFormChange={handleFormChange}
          onSuggestionChange={handleSuggestionChange}
          onRemoveSuggestion={handleRemoveSuggestion}
          onAddSuggestion={handleAddSuggestion}
          onToolCreated={handleToolCreated}
          onChangeReason={handleChangeReason}
        />
      </div>
      <QuoteEditorActions
        canMarkAsQuoted={canMarkAsQuoted && !isSubmitting}
        canSendQuote={canSendQuote && !isSubmitting}
        isSubmitting={isSubmitting}
        onMarkAsQuoted={handleMarkAsQuoted}
        onSendQuote={handleSendQuote}
      />
      {isConfirmationOpen && (
        <QuoteConfirmationModal
          quoteId={quoteDetails.id}
          clientName={quoteDetails.organizationName}
          countryName={quoteDetails.requestedCountry.name}
          countryCode={quoteDetails.requestedCountry.code}
          quotedToolsCount={quotedCount}
          totalAmount={formatTotalQuoted()}
          organizationId={quoteDetails.organizationId}
          onClose={closeConfirmation}
          onConfirm={handleConfirmSendQuote}
        />
      )}
      {isSuggestReasonOpen && (
        <SuggestReasonModal device={selectedItem} onClose={closeSuggestReason} onConfirm={handleConfirmSuggestReason} />
      )}
      {isSendingQuote && <QuoteLoaderModal />}
    </div>
  );
};

export default QuoteEditorModule;
